import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Spin,
  Row,
  Col,
  Space,
  Tooltip,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StoreState } from "../../../enum/StoreState";
import { useStores } from "../../../stores/setup/use-store";
import ChangePasswordModal from "../../../pages/Login/ChangePassword";
import "./MyData.css";
import { useTranslation } from "react-i18next";
import { Box, useDisclosure, useToast } from "@chakra-ui/react";
import { InternationalPhoneInput } from "../../../components/InternationalPhoneInput/InternationalPhoneInput";
import { SCREEN_HORIZONTAL_SPACING } from "../../../constants";
import { CheckCircle, XCircle, Edit } from "lucide-react";
import { PhoneVerificationModal } from "./components/PhoneVerificationModal";

interface FormMyDataValues {
  name: string;
  lastName: string;
  middleName: string;
  login: string;
  email: string;
  area: number;
  position: number;
}

const MyData: React.FC = () => {
  const {
    dataStores: { userConfigStore, authStore },
  } = useStores();
  const [form] = Form.useForm();
  const [phoneData, setPhoneData] = useState<{ phone?: string; code?: string }>(
    {}
  );
  const [isPhoneEdited, setIsPhoneEdited] = useState<boolean>(false);
  const { t } = useTranslation("translation", {
    keyPrefix: "admin.myData",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    userConfigStore.getUserConfigApi();
    userConfigStore.getUserAreaApi();
    userConfigStore.getUserPositionApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPhoneData({
      code: userConfigStore.userConfig.phone_code,
      phone: userConfigStore.userConfig.phone_number,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userConfigStore.userConfig, isOpen]);

  useEffect(() => {
    if (userConfigStore.userConfig.login !== "") {
      const {
        name,
        middle_name,
        last_name,
        email,
        login,
        area_id,
        position_id,
        phone_code,
        phone_number,
      } = userConfigStore.userConfig;

      form.setFieldsValue({
        name: name,
        lastName: last_name,
        middleName: middle_name,
        login: login,
        email: email,
        area: area_id > 0 ? area_id : "",
        position: position_id > 0 ? position_id : "",
      });

      setPhoneData({
        code: phone_code || "55",
        phone: phone_number,
      });

      setIsPhoneEdited(false);
    }
  }, [form, userConfigStore, userConfigStore.userConfig]);

  const handleOnEdit = () => {
    const formValues: FormMyDataValues = form.getFieldsValue();

    if (formValues) {
      const { name, lastName, middleName, email, area, position } = formValues;

      if (!area || !position) {
        return;
      }

      userConfigStore
        .updateUserConfig({
          name,
          lastName,
          email,
          areaId: area,
          positionId: position,
          middleName,
          phoneCode: phoneData.code,
          phoneNumber: phoneData.phone,
        })
        .then(() => {
          if (isPhoneEdited) {
            userConfigStore.setPhoneVerified(false);
            setIsPhoneEdited(false);
          }
        });
    }
  };

  const handleOpenUpdatePhoneModal = () => {
    onOpen();
  };

  const handlePhoneUpdateSuccess = useCallback(
    async (code?: string, phone?: string) => {
      const formValues: FormMyDataValues = form.getFieldsValue();

      if (!code || !phone) return;

      const { name, lastName, middleName, email, area, position } = formValues;

      await userConfigStore.updateUserConfig({
        name,
        lastName,
        email,
        areaId: area,
        positionId: position,
        middleName,
        phoneCode: code,
        phoneNumber: phone,
      });
      userConfigStore.setPhoneVerified(true);
      onClose();

      toast({
        title: t("phoneSuccessfullyUpdated"),
        status: "success",
        position: "bottom-right",
        isClosable: true,
      });
    },
    [userConfigStore, onClose, t, toast]
  );

  const isPhoneAlreadyVerified = useMemo(() => {
    if (!phoneData.code || !phoneData.phone) {
      return false;
    }

    return (
      userConfigStore.userConfig.phone_verified &&
      phoneData.code === userConfigStore.userConfig.phone_code &&
      phoneData.phone === userConfigStore.userConfig.phone_number
    );
  }, [
    phoneData.code,
    phoneData.phone,
    userConfigStore.userConfig.phone_code,
    userConfigStore.userConfig.phone_number,
    userConfigStore.userConfig.phone_verified,
  ]);

  return (
    <Box mt={8} mb={8} mx={SCREEN_HORIZONTAL_SPACING}>
      {userConfigStore.state === StoreState.ERROR ? (
        <Alert
          message={t("error")}
          description={userConfigStore.messageError}
          type="error"
          showIcon
          closable
        />
      ) : (
        ""
      )}

      <PhoneVerificationModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handlePhoneUpdateSuccess}
        initialPhoneData={phoneData}
        title={t("updatePhoneNumber") as string}
        description={t("enterYourNewPhone") as string}
        confirmButtonText={t("sendVerificationCode") as string}
        isPhoneAlreadyVerified={isPhoneAlreadyVerified}
      />

      {userConfigStore.state === StoreState.SUCCESS ? (
        <Alert
          message={t("success")}
          description={t("dataEditedSuccess")}
          type="success"
          showIcon
          closable
        />
      ) : (
        ""
      )}

      <ChangePasswordModal
        onCancel={() => authStore.setModalChangePasswordVisible(false)}
      />

      <Card title={t("myData")}>
        <Form
          form={form}
          layout="vertical"
          name="form"
          size="large"
          initialValues={{ modifier: "public" }}
          style={{ maxWidth: 800, margin: "0 auto" }}
        >
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12} md={7}>
              <Form.Item
                name="name"
                label={t("name")}
                rules={[
                  {
                    required: true,
                    message: t("pleaseInputYourName") as string,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={10}>
              <Form.Item name="Phone" label={t("phone")}>
                <div style={{ position: "relative" }}>
                  <InternationalPhoneInput
                    style={{ width: "100%", paddingRight: "40px" }}
                    phoneData={phoneData}
                    setPhoneData={setPhoneData}
                    disabled={true}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {userConfigStore.userConfig.phone_verified &&
                    userConfigStore.userConfig.phone_code &&
                    userConfigStore.userConfig.phone_number ? (
                      <Tooltip title={t("verifiedNumber")} placement="top">
                        <CheckCircle
                          size={18}
                          color="#52c41a"
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("numberNotVerified")} placement="top">
                        <XCircle
                          size={18}
                          color="#ff4d4f"
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={7}>
              <Button
                type="primary"
                icon={<Edit size={16} />}
                style={{
                  width: "100%",
                  marginBottom: 24,
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
                onClick={handleOpenUpdatePhoneModal}
              >
                {t("updateNumber")}
              </Button>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="middleName" label={t("middleName")}>
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="lastName"
                label={t("lastName")}
                rules={[
                  {
                    required: true,
                    message: t("pleaseInputYourLastName") as string,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="login" label="Login">
                <Input readOnly={true} style={{ backgroundColor: "#f5f5f5" }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Button
                type="primary"
                style={{ width: "100%", marginBottom: 24, height: "40px" }}
                onClick={() => authStore.setModalChangePasswordVisible(true)}
              >
                {t("changePassword")}
              </Button>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                name="email"
                label={t("email")}
                rules={[
                  {
                    required: true,
                    message: t("pleaseInputYourEmail") as string,
                  },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="position"
                label={t("selectYourJobPosition")}
                rules={[
                  {
                    required: true,
                    message: t("pleaseSelectJobPosition") as string,
                  },
                ]}
              >
                <Select id="selectPosition">
                  {userConfigStore.userPosition.map((value) => (
                    <Select.Option value={value.id} key={value.id}>
                      {value.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="area"
                label={t("selectYourJobArea")}
                rules={[
                  {
                    required: true,
                    message: t("pleaseSelectJobArea") as string,
                  },
                ]}
              >
                <Select id="selectArea">
                  {userConfigStore.userArea.map((value) => (
                    <Select.Option value={value.id} key={value.id}>
                      {value.description}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row justify="end" style={{ marginTop: 24 }}>
            <Col>
              <Space>
                <Spin spinning={userConfigStore.state === StoreState.PEDDING} />
                <Button
                  type="primary"
                  htmlType="submit"
                  style={{
                    backgroundColor: "orange",
                    borderColor: "orange",
                    height: "40px",
                  }}
                  onClick={handleOnEdit}
                >
                  {t("edit")}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </Box>
  );
};

export default observer(MyData);
